import React, { FC, useContext, useState } from 'react'
import { AnalyticsContext } from '../AnalyticsContext'
import {
  AnalyticsResourceVersionsOverviewFilter,
  ResourceVersionsOverviewOrderColumn,
  SqlOrderDirectionEnum
} from '../../../graphql/private/PrivateSchemaTypes'
import { getPagesCount } from '../utils'
import { Column } from 'react-table'
import { t, Trans } from '@lingui/macro'
import { formatDateShort } from '../../../utils/dateHelpers'
import TableExtendedOld from '../../../components/common/table-extended-old/TableExtendedOld'
import { useResourceVersionsOverviewTableQuery } from './ResourceVersionsTable.codegen'
import { Box, Flex, Text } from 'front-end/src/components'
import { StatusLatestGrey } from '../../../components/common/status/Status'
import { brandColors } from '../../../../../shared/basicPalette'
import IconOptionsVertical from '../../../components/common/icon/IconOptionsVertical'
import { Dropdown } from '../../../components/common/dropdown/Dropdown'
import { IconVersions } from '../../../components/common/icon/IconVersions'
import { navigateTo } from '../../../app/util/appHistory'
import { linkBuilder } from '../../../../../shared/linkBuilder'
import { DownloadResourceVersionsCSV } from './DownloadResourceVersionsCSV'
import { TopicVersionSmallMarbleRating } from 'front-end/src/components/common/marble-rating/TopicVersionSmallMarbleRating'

type IProps = {
  resourceId: number
  productionVersionId?: number
}

export type VersionOverviewTableFilters =
  AnalyticsResourceVersionsOverviewFilter & {
    page: number
    limit: number
    orderColumn: ResourceVersionsOverviewOrderColumn
  }

export const VersionOverviewTable: FC<IProps> = ({
  resourceId,
  productionVersionId
}: IProps) => {
  const { filterAsQueryVariables } = useContext(AnalyticsContext)
  const [versionsTableFilters, setVersionsTableFilters] =
    useState<VersionOverviewTableFilters>({
      page: 1,
      limit: 6,
      orderColumn: ResourceVersionsOverviewOrderColumn.version,
      orderDirection: SqlOrderDirectionEnum.DESC,
      resourceId
    })

  const { data, loading } = useResourceVersionsOverviewTableQuery({
    variables: {
      mainAnalyticsFilter: filterAsQueryVariables,
      overviewFilter: versionsTableFilters
    }
  })

  let pagesCount = 1

  if (data && data.analytics) {
    pagesCount = getPagesCount(
      data.analytics.resources.versionsOverview?.count,
      versionsTableFilters.limit
    )
  }

  const tableColumns: Array<Column<any>> = [
    {
      Header: t`Version`,
      id: ResourceVersionsOverviewOrderColumn.version,
      Cell: (row) => (
        <Box>
          <Flex align="center">
            <Text
              fontWeight="700"
              mr="8px"
              fontSize="lg"
              color={brandColors.grey.dark}
            >
              {row.original.version}
            </Text>
            {productionVersionId === row.original.id && <StatusLatestGrey />}
          </Flex>
          {row.original.publishingMessage && (
            <Box
              mt="8px"
              whiteSpace="normal"
              fontSize="sm"
              color={brandColors.grey.mid}
            >
              {row.original.publishingMessage}
            </Box>
          )}
        </Box>
      )
    },
    {
      Header: t`Update date`,
      width: 120,
      accessor: 'createdAt',
      className: 'date',
      id: ResourceVersionsOverviewOrderColumn.createdAt,
      Cell: (row) => formatDateShort(row.value)
    },
    {
      Header: t`Published by`,
      width: 200,
      accessor: 'publishedByUserName',
      id: ResourceVersionsOverviewOrderColumn.publishedBy
    },
    {
      Header: t`Avg. rating`,
      className: 'overflowVisible',
      id: ResourceVersionsOverviewOrderColumn.avgRating,
      maxWidth: 150,
      Cell: (row) =>
        row.original.totalRating > 0 && (
          <TopicVersionSmallMarbleRating
            topicVersionId={row.original.id}
            topicId={resourceId}
            numberRated={row.original.numberRated}
            rating={row.original.avgRating}
          />
        )
    },
    {
      Header: t`Views`,
      id: ResourceVersionsOverviewOrderColumn.viewsCount,
      maxWidth: 100,
      accessor: 'viewsCount',
      Cell: (row) => <span className="green">{row.value}</span>
    },
    {
      Header: t`Last viewed`,
      id: ResourceVersionsOverviewOrderColumn.lastViewedAt,
      width: 120,
      accessor: 'lastViewedAt',
      Cell: (row) => row.value && formatDateShort(row.value)
    },
    {
      width: 60,
      sortable: false,
      Cell: (row) => (
        <Dropdown
          tabIndex={undefined}
          ml="-3px"
          content={
            <a
              className="DropdownLink"
              onClick={() =>
                navigateTo(
                  productionVersionId === row.original.id
                    ? linkBuilder.topicUrl(resourceId)
                    : linkBuilder.topicUrlWithVersion(
                        resourceId,
                        row.original.version
                      )
                )
              }
            >
              <IconVersions w="18px" h="16px" />
              <Trans>View resource version</Trans>
            </a>
          }
        >
          <IconOptionsVertical />
        </Dropdown>
      )
    }
  ]

  return (
    <TableExtendedOld
      loading={loading}
      manual
      data={data?.analytics.resources.versionsOverview?.items}
      columns={tableColumns}
      className="-highlight AnalyticsTable"
      defaultPageSize={versionsTableFilters.limit}
      showPageSizeOptions={false}
      resizable={false}
      minRows={1}
      pages={pagesCount}
      paginationContentAfter={
        data && (
          <DownloadResourceVersionsCSV
            overviewFilter={versionsTableFilters}
            resourceVersionsCount={
              data.analytics.resources.versionsOverview?.count || 0
            }
          />
        )
      }
      defaultSortDesc
      defaultSorted={[
        {
          id: versionsTableFilters.orderColumn,
          desc:
            versionsTableFilters.orderDirection === SqlOrderDirectionEnum.DESC
        }
      ]}
      onSortedChange={(newSorted) => {
        setVersionsTableFilters((prevState) => {
          const descendingOrder = newSorted[0] ? newSorted[0].desc : false

          return {
            ...prevState,
            orderColumn: newSorted[0]
              ? (newSorted[0].id as ResourceVersionsOverviewOrderColumn)
              : ResourceVersionsOverviewOrderColumn.version,
            orderDirection: descendingOrder
              ? SqlOrderDirectionEnum.DESC
              : SqlOrderDirectionEnum.ASC
          }
        })
      }}
      onPageChange={(newPage) => {
        setVersionsTableFilters((prevState) => {
          return {
            ...prevState,
            page: newPage + 1
          }
        })
      }}
      page={versionsTableFilters.page - 1}
      onSearchChange={(searchVal) => {
        setVersionsTableFilters((prevState) => {
          return {
            ...prevState,
            search: searchVal
          }
        })
      }}
    />
  )
}
